import React from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Form,
  TextArea,
  Dimmer,
  Loader,
} from "semantic-ui-react";
import "./timeOffModal.css";
import { DropDownList } from "../../common";
import FetchingSpinner from "../../common/FetchingSpinner";
import { updateLeaveStatus } from "../leaveApproval/leaveApprovalActions";
import { useDispatch } from "react-redux";

function getLeaveInfoMessage({ StartDate, EndDate, StartingLeave, EndingLeave }, t) {
  if (!StartDate || !EndDate) return null;
  if (!t) return null;

  const startDate = new Date(StartDate);
  const endDate = new Date(EndDate);

  if (endDate < startDate) {
    return "⚠️ " + t("app.modals.addLeave.invalidDateRange");
  }

  const timeDiff = endDate.getTime() - startDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
  const isSingleDay = new Date(StartDate).toDateString() === new Date(EndDate).toDateString();

  if (isSingleDay) {
    if (StartingLeave === 0 && EndingLeave === 0) return "🕑 " + t("app.modals.addLeave.firstHalfOnly");
    if (StartingLeave === 1 && EndingLeave === 1) return "🕑 " + t("app.modals.addLeave.secondHalfOnly");
    if (StartingLeave === 0 && EndingLeave === 1) return "🕑 " + t("app.modals.addLeave.fullDay");
    return "🕑 " + t("app.modals.addLeave.customPeriod");
  } else {
    const firstDay =
      StartingLeave === 0
        ? t("app.modals.addLeave.multiDayFirst")
        : StartingLeave === 1
          ? t("app.modals.addLeave.multiDayFirstSecondHalf")
          : t("app.modals.addLeave.multiDayCustom");
    const lastDay =
      EndingLeave === 0
        ? t("app.modals.addLeave.multiDayLastFirstHalf")
        : EndingLeave === 1
          ? t("app.modals.addLeave.multiDayLastFull")
          : t("app.modals.addLeave.multiDayCustom");
    return "📅 " + t("app.modals.addLeave.multiDayLabel", { days: daysDiff, first: firstDay, last: lastDay, middle: t("app.modals.addLeave.multiDayMiddle") });
  }
}

const TImeOffModal = ({
  IsModalOpen,
  handleCloseModal,
  onChange,
  handleDateChange,
  onSave,
  onCancelLeaveSuccess,
  timeOffModal,
  updateDropDown,
  isFormLoading = false,
  leaveTypeOptions,
  startingLeaveOptions,
  endingLeaveOptions,
  isLoadingLeaveTypes = false,
  isEditMode = false,
  validation = null,
  validateLeaveAvailability,
  isAdmin = false
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showConflictsModal, setShowConflictsModal] = React.useState(false);
  const infoMessage = getLeaveInfoMessage(timeOffModal, t);

  // Check if the leave is approved
  const isLeaveApproved = timeOffModal?.Approvals &&
    Array.isArray(timeOffModal.Approvals) &&
    timeOffModal.Approvals.length > 0 &&
    timeOffModal.Approvals[0]?.approvalStatusText === "Approved";

  // Check if the leave is pending
  const isLeavePending = timeOffModal?.Approvals &&
    Array.isArray(timeOffModal.Approvals) &&
    timeOffModal.Approvals.length > 0 &&
    timeOffModal.Approvals[0]?.approvalStatusText === "Pending";

  // Check if approverId is null (no approval assigned yet) - for admin Cancel Leave in edit mode
  const isApproverIdNull = !timeOffModal?.Approvals?.length ||
    timeOffModal.Approvals[0]?.approverId == null ||
    timeOffModal.Approvals[0]?.approverId === undefined;

  // Check if this is a public holiday
  const isPublicHoliday = timeOffModal?.IsPublicHoliday === true;
  const publicHolidayName = timeOffModal?.PublicHolidayName || "";

  // Handle cancel leave action (works for approved/pending leave or admin cancelling leave with no approval)
  const handleCancelLeave = () => {
    const groupId = timeOffModal?.Approvals?.[0]?.groupId ?? timeOffModal?.GroupId;
    if (groupId) {
      const approvalData = {
        groupId,
        comments: "Leave cancelled by employee", // Default comment for cancellation
        status: 3, // 3 = Cancelled
      };

      dispatch(updateLeaveStatus(approvalData, "approved", { refreshApprovalList: false }))
        .then((data) => {
          if (data?.success && typeof onCancelLeaveSuccess === "function") {
            onCancelLeaveSuccess();
          }
          handleCloseModal();
        })
        .catch(() => {
          handleCloseModal();
        });
    }
  };

  // Calculate remaining balance and division from selected leave type
  const getRemainingBalanceAndDivision = () => {
    const effectiveLeaveTypeId = timeOffModal.LeaveTypeId;

    if (!effectiveLeaveTypeId || !leaveTypeOptions || leaveTypeOptions.length === 0) {
      return { balance: 0, division: "Days" };
    }

    const selectedLeaveType = leaveTypeOptions.find(option => option.key === effectiveLeaveTypeId);
    if (!selectedLeaveType) {
      return { balance: 0, division: "Days" };
    }

    return {
      balance: selectedLeaveType.leavesCount || 0,
      division: selectedLeaveType.leaveDivision || "Days"
    };
  };

  // Get remaining balance from validation result if available, otherwise fall back to local calculation
  const getRemainingBalanceDisplay = () => {
    // If validation is loading, show loading state
    if (validation && validation.isLoading) {
      return {
        balance: t("app.common.loading"),
        division: "",
        isLoading: true,
        remainingDays: 0,
        remainingHours: 0,
        totalDays: 0,
        totalHours: 0
      };
    }

    // If validation result has remaining balance, use it
    if (validation && validation.result && validation.result.remainingBalance !== undefined) {
      return {
        balance: validation.result.remainingBalance,
        division: validation.result.leaveDivision || "Days",
        remainingDays: validation.result.remainingUserLeavesInDays || 0,
        remainingHours: validation.result.remainingUserLeavesInHours || 0,
        totalDays: validation.result.totalUserLeavesInDays || 0,
        totalHours: validation.result.totalUserLeavesInHours || 0,
        isLoading: false
      };
    }

    // Fall back to local calculation
    const { balance, division } = getRemainingBalanceAndDivision();
    return {
      balance,
      division,
      isLoading: false,
      remainingDays: 0,
      remainingHours: 0,
      totalDays: 0,
      totalHours: 0
    };
  };

  const {
    balance: remainingBalance,
    division: leaveDivision,
    isLoading: isBalanceLoading,
    remainingDays: remainingUserLeavesInDays,
    remainingHours: remainingUserLeavesInHours,
    totalDays: totalUserLeavesInDays,
    totalHours: totalUserLeavesInHours
  } = getRemainingBalanceDisplay();

  // Track if we've already set the default leave type for this modal session
  const hasSetDefaultLeaveType = React.useRef(false);

  // Set default leave type only once when modal first opens (not on date/period changes)
  React.useEffect(() => {
    // Only set default if:
    // 1. Modal is open
    // 2. Not in edit mode
    // 3. Leave types are loaded
    // 4. No leave type is currently selected
    // 5. We haven't already set the default for this session
    if (
      IsModalOpen &&
      !isEditMode &&
      !isLoadingLeaveTypes &&
      !timeOffModal.LeaveTypeId &&
      !hasSetDefaultLeaveType.current &&
      leaveTypeOptions &&
      leaveTypeOptions.length > 0
    ) {
      // Find first non-header leave type
      const firstLeaveType = leaveTypeOptions.find(option => !option.isHeader && (option.key || option.value));
      if (firstLeaveType) {
        const defaultLeaveTypeId = firstLeaveType.key || firstLeaveType.value;
        // Update the leave type via onChange
        const syntheticEvent = {
          target: {
            name: "LeaveTypeId",
            value: defaultLeaveTypeId
          }
        };
        const data = {
          name: "LeaveTypeId",
          value: defaultLeaveTypeId,
          key: defaultLeaveTypeId,
          options: leaveTypeOptions || [] // Add options array that updateDropDownState expects
        };
        updateDropDown(syntheticEvent, data);
        hasSetDefaultLeaveType.current = true;
      }
    }

    // Reset the flag when modal closes
    if (!IsModalOpen) {
      hasSetDefaultLeaveType.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [IsModalOpen, isEditMode, isLoadingLeaveTypes, leaveTypeOptions]);

  // Check if selected leave type has deducted property set to true
  const effectiveLeaveTypeId = timeOffModal.LeaveTypeId;
  const selectedLeaveType = effectiveLeaveTypeId && leaveTypeOptions
    ? leaveTypeOptions.find(option => option.key === effectiveLeaveTypeId)
    : null;
  const isDeducted = selectedLeaveType?.deducted === true;

  // Check if button should be disabled
  const hasLeaveTypeSelected = timeOffModal.LeaveTypeId && timeOffModal.LeaveTypeId !== "";
  const hasValidationError = validation && validation.result && !validation.result.isAvailable;

  // Check for invalid date range
  const hasInvalidDateRange = timeOffModal.StartDate && timeOffModal.EndDate &&
    new Date(timeOffModal.EndDate) < new Date(timeOffModal.StartDate);

  // For deductible leaves, check remaining balance. For non-deductible leaves, don't check balance
  // However, if validation has been run and shows isAvailable: true, trust the validation result
  // and don't check balance separately (validation already accounts for balance).
  // In edit mode, if validation hasn't been run yet (user hasn't made changes), allow the update
  // since the leave already exists and was valid when created.
  const hasValidationResult = validation && validation.result && !validation.isLoading;
  const shouldTrustValidation = hasValidationResult && validation.result.isAvailable;
  const shouldSkipBalanceCheck = shouldTrustValidation || (isEditMode && !hasValidationResult);
  const hasInsufficientBalance = !shouldSkipBalanceCheck && isDeducted && hasLeaveTypeSelected && remainingBalance <= 0;

  const isButtonDisabled = !hasLeaveTypeSelected || hasInsufficientBalance || hasValidationError || hasInvalidDateRange;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          /* Override global .ui.dropdown .menu { background: #065a89 } so modal DDLs stay light in production */
          .time-off-modal-dropdown.ui.dropdown .menu {
            background: #ffffff !important;
            border: 1px solid #e9ecef !important;
            border-radius: 8px !important;
          }
          .time-off-modal-dropdown.ui.dropdown .menu .item {
            background: #ffffff !important;
            color: #000000 !important;
            border-bottom: 1px solid #e9ecef !important;
          }
          .time-off-modal-dropdown.ui.dropdown .menu .item:hover {
            background: rgba(102, 126, 234, 0.1) !important;
            color: #000000 !important;
          }
          .time-off-modal-dropdown.ui.dropdown .menu .item.active {
            background: rgba(102, 126, 234, 0.1) !important;
            color: #000000 !important;
          }
          .time-off-modal-dropdown.ui.dropdown .menu .item.selected {
            background: rgba(102, 126, 234, 0.15) !important;
            color: #000000 !important;
            font-weight: 600 !important;
          }
          /* Header styling for non-deductible leaves */
          .time-off-modal-dropdown.ui.dropdown .menu .item.leave-type-header {
            cursor: default !important;
            pointer-events: none !important;
            background-color: #f8f9fa !important;
            font-weight: 600 !important;
            font-size: 0.85rem !important;
            color: #6c757d !important;
            text-transform: uppercase !important;
            letter-spacing: 0.5px !important;
            padding: 8px 12px !important;
            border-top: 1px solid #e9ecef !important;
            border-bottom: 1px solid #e9ecef !important;
            margin: 4px 0 !important;
          }
          .time-off-modal-dropdown.ui.dropdown .menu .item.leave-type-header:hover {
            background-color: #f8f9fa !important;
            color: #6c757d !important;
          }
          /* Icon styling in dropdown items */
          .time-off-modal-dropdown.ui.dropdown .menu .item span {
            display: flex;
            align-items: center;
          }
        `}
      </style>
      {IsModalOpen && (
        <div
          className="time-off-modal-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(2px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflow: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget && !isFormLoading) {
              handleCloseModal();
            }
          }}
        >
          <div
            className="time-off-modal-wrapper"
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '600px',
              maxHeight: 'min(90vh, 90dvh)',
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              flexShrink: 0,
            }}
          >
            <Dimmer.Dimmable dimmed={isFormLoading} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <Dimmer active={isFormLoading} inverted>
                <Loader inverted content={t("app.modals.addLeave.saving")} size="big" />
              </Dimmer>
              <div className="modalInner" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* Header Section */}
                <div className="modal-header-brand time-off-modal-header" style={{ position: "relative", flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => !isFormLoading && handleCloseModal()}
                    disabled={isFormLoading}
                    style={{
                      position: "absolute",
                      top: "20px",
                      right: "20px",
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      color: "white",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: isFormLoading ? "not-allowed" : "pointer",
                      opacity: isFormLoading ? 0.6 : 1,
                      fontSize: "20px",
                      fontWeight: "bold",
                      transition: "all 0.3s ease",
                      lineHeight: "1",
                      padding: "0",
                      margin: "0",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)";
                      e.currentTarget.style.transform = "scale(1.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)";
                      e.currentTarget.style.transform = "scale(1)";
                    }}
                  >
                    ×
                  </button>
                  <div className="d-flex align-items-center">
                    <div
                      className="me-3"
                      style={{
                        width: "48px",
                        height: "48px",
                        borderRadius: "50%",
                        background: "rgba(255, 255, 255, 0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                        <path d={isLeaveApproved ? "M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16zM4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" : (isEditMode ? "M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09zM4.157 8.5H7a.5.5 0 0 1 .478.647L6.11 13.59l5.732-6.09H9a.5.5 0 0 1-.478-.647L9.89 2.41 4.157 8.5z" : "M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z")} />
                      </svg>
                    </div>
                    <div>
                      <h3 className="mb-1 fw-bold" style={{ fontSize: "1.5rem" }}>
                        {isPublicHoliday ? t("app.modals.addLeave.publicHoliday") : (isLeaveApproved ? t("app.modals.addLeave.cancelLeave") : (isEditMode ? t("app.modals.addLeave.editLeave") : t("app.modals.addLeave.addLeaveToCalendar")))}
                      </h3>
                      <p className="mb-0 text-white-50" style={{ fontSize: "0.9rem" }}>
                        {isPublicHoliday ? (publicHolidayName ? t("app.modals.addLeave.subtitlePublicHolidayName", { name: publicHolidayName }) : t("app.modals.addLeave.subtitlePublicHoliday")) : (isLeaveApproved ? t("app.modals.addLeave.subtitleCancel") : (isEditMode ? t("app.modals.addLeave.subtitleEdit") : t("app.modals.addLeave.subtitleAdd")))}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Form Content - scrollable so textbox/buttons stay visible on small viewports */}
                <div className="time-off-modal-body" style={{ padding: "24px", flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>

                  <Form>
                    {/* Public Holiday Name Display or Leave Type and Remaining Balance */}
                    {isPublicHoliday ? (
                      <div style={{ marginBottom: 20 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          {t("app.modals.addLeave.publicHoliday")}
                        </label>
                        <div style={{
                          padding: "12px 16px",
                          backgroundColor: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #e9ecef",
                          fontSize: "1rem",
                          color: "#212529",
                          fontWeight: 500
                        }}>
                          {publicHolidayName || t("app.modals.addLeave.publicHoliday")}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 20, gap: 16 }}>
                        <div style={{ flex: 2 }}>
                          <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                            {t("app.modals.addLeave.leaveType")}
                          </label>
                          <DropDownList
                            className="time-off-modal-dropdown"
                            selection
                            fluid
                            value={timeOffModal.LeaveTypeId || ""}
                            options={leaveTypeOptions || []}
                            name="LeaveTypeId"
                            onChange={isLeaveApproved ? undefined : updateDropDown}
                            style={{
                              border: "1px solid #e9ecef",
                              borderRadius: "8px",
                              ...(isLeaveApproved && {
                                backgroundColor: "#f8f9fa",
                                color: "#6c757d",
                                cursor: "not-allowed"
                              })
                            }}
                            placeholder={isLoadingLeaveTypes ? t("app.modals.addLeave.loadingLeaveTypes") : t("app.modals.addLeave.selectLeaveType")}
                            disabled={isLoadingLeaveTypes || isLeaveApproved}
                          />
                          {/* Total Allowance below dropdown */}
                          {isDeducted && (totalUserLeavesInDays !== undefined && totalUserLeavesInHours !== undefined && (totalUserLeavesInDays > 0 || totalUserLeavesInHours > 0)) && (
                            <div style={{ marginTop: '8px', padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                              <span style={{ fontSize: "0.8rem", color: "#6c757d", fontWeight: 500 }}>
                                {t("app.modals.addLeave.totalAllowance")}: {totalUserLeavesInDays || 0} {t("app.modals.addLeave.days")} / {totalUserLeavesInHours || 0} {t("app.modals.addLeave.hours")}
                              </span>
                            </div>
                          )}
                        </div>
                        {!isPublicHoliday && isDeducted && timeOffModal.LeaveTypeId && (
                          <div style={{
                            flex: 1,
                            minWidth: 200,
                            padding: '12px 16px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            border: '1px solid #e9ecef',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: '8px'
                          }}>
                            <div style={{ fontSize: "0.85rem", color: "#6c757d", fontWeight: 500 }}>
                              {t("app.modals.addLeave.remainingBalance")}
                            </div>
                            <div style={{ fontSize: "1.1rem", color: "#212529", fontWeight: 600 }}>
                              {remainingBalance !== undefined ? remainingBalance : 'N/A'}
                            </div>
                            <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                              {leaveDivision === "Days" ? t("app.modals.addLeave.days") : t("app.modals.addLeave.hours")}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Total Days and Status in Edit Mode */}
                    {isEditMode && (timeOffModal.TotalDays || (timeOffModal?.Approvals && Array.isArray(timeOffModal.Approvals) && timeOffModal.Approvals.length > 0)) && (
                      <div style={{ display: "flex", alignItems: "center", marginBottom: 20, gap: "16px" }}>
                        {timeOffModal.TotalDays && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "0.9rem", color: "#495057", fontWeight: 600 }}>{t("app.modals.addLeave.totalDays")}:</span>
                            <span style={{
                              fontSize: "1rem",
                              color: "#495057",
                              fontWeight: 700,
                              padding: '4px 8px',
                              backgroundColor: '#e9ecef',
                              borderRadius: '6px',
                              border: '1px solid #dee2e6'
                            }}>
                              {timeOffModal.TotalDays}
                            </span>
                          </div>
                        )}
                        {timeOffModal?.Approvals && Array.isArray(timeOffModal.Approvals) && timeOffModal.Approvals.length > 0 && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span style={{ fontSize: "0.9rem", color: "#495057", fontWeight: 600 }}>{t("app.modals.addLeave.status")}:</span>
                            <span style={{
                              fontSize: "1rem",
                              color: "#fff",
                              fontWeight: 700,
                              padding: '4px 8px',
                              backgroundColor: timeOffModal.Approvals[0].approvalStatus === 1 ? '#28a745' : '#ffc107',
                              borderRadius: '6px',
                              border: '1px solid transparent'
                            }}>
                              {timeOffModal.Approvals[0].approvalStatusText || "Pending"}
                            </span>
                          </div>
                        )}
                        {(timeOffModal.IsAutoDeducted || timeOffModal.isAutoDeducted) && (
                          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <span
                              title={t("app.modals.addLeave.autoDeductedTooltip")}
                              style={{
                                fontSize: "0.85rem",
                                color: "#1e3a5f",
                                fontWeight: 700,
                                padding: "4px 8px",
                                backgroundColor: "#e8f0fe",
                                borderRadius: "6px",
                                border: "1px solid #c5d7f5",
                              }}
                            >
                              {t("app.modals.addLeave.autoDeducted")}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Compact Info Message */}
                    {infoMessage && (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#0c5460",
                          marginBottom: "12px",
                          padding: "8px 12px",
                          backgroundColor: "#e3f7fa",
                          borderRadius: "6px",
                          border: "1px solid #b8e2ea",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >

                        <span style={{ fontWeight: "500" }}>{infoMessage}</span>
                      </div>
                    )}

                    {/* Compact Validation Message */}
                    {validation && validation.result && (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: validation.result.isAvailable ? "#155724" : "#721c24",
                          marginBottom: "12px",
                          padding: "8px 12px",
                          backgroundColor: validation.result.isAvailable ? "#d4edda" : "#f8d7da",
                          borderRadius: "6px",
                          border: `1px solid ${validation.result.isAvailable ? "#c3e6cb" : "#f5c6cb"}`,
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <span style={{ fontSize: "1.1em" }}>
                          {validation.result.isAvailable ? "✅" : "❌"}
                        </span>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ fontWeight: "500" }}>
                            {validation.result.isAvailable
                              ? t("app.modals.addLeave.leaveAvailable")
                              : validation.result.message || t("app.modals.addLeave.leaveNotAvailable")
                            }
                          </span>
                          {validation.result.conflicts && validation.result.conflicts.length > 0 && (
                            <div style={{ marginTop: "8px", textAlign: "center" }}>
                              <button
                                onClick={() => setShowConflictsModal(true)}
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#721c24",
                                  backgroundColor: "transparent",
                                  border: "1px solid #f5c6cb",
                                  borderRadius: "4px",
                                  padding: "4px 12px",
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  fontWeight: "500"
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = "#721c24";
                                  e.currentTarget.style.color = "white";
                                  e.currentTarget.style.borderColor = "#721c24";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = "transparent";
                                  e.currentTarget.style.color = "#721c24";
                                  e.currentTarget.style.borderColor = "#f5c6cb";
                                }}
                              >
                                {t("app.modals.addLeave.readMoreDetails")}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Compact Validation Loading */}
                    {validation && validation.isLoading && (
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#0c5460",
                          marginBottom: "12px",
                          padding: "8px 12px",
                          backgroundColor: "#e3f7fa",
                          borderRadius: "6px",
                          border: "1px solid #b8e2ea",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <div
                          style={{
                            width: "14px",
                            height: "14px",
                            border: "2px solid #0c5460",
                            borderTop: "2px solid transparent",
                            borderRadius: "50%",
                            animation: "spin 1s linear infinite",
                          }}
                        />
                        <span style={{ fontWeight: "500" }}>{t("app.modals.addLeave.validatingAvailability")}</span>
                      </div>
                    )}

                    {/* Dates and Periods in two columns */}
                    <div style={{ display: "flex", gap: 16 }}>
                      {/* From Date & Morning Period */}
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          {t("app.modals.addLeave.fromDate")}
                        </label>
                        <DatePicker
                          name="StartDate"
                          selected={timeOffModal.StartDate ? new Date(timeOffModal.StartDate) : null}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          placeholderText={t("app.modals.addLeave.selectStartDate")}
                          dateFormat="MM/dd/yyyy"
                          disabled={isLeaveApproved}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "border-color 0.3s ease",
                            ...(isLeaveApproved && {
                              backgroundColor: "#f8f9fa",
                              color: "#6c757d",
                              cursor: "not-allowed"
                            })
                          }}
                          onChange={isLeaveApproved ? undefined : (date) => handleDateChange("StartDate", date)}
                        />
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, marginTop: 12, display: "block" }}>
                          {t("app.modals.addLeave.morningPeriod")}
                        </label>
                        <DropDownList
                          className="time-off-modal-dropdown"
                          selection
                          defaultValue={0}
                          options={startingLeaveOptions}
                          fluid
                          name="StartingLeave"
                          value={timeOffModal.StartingLeave}
                          onChange={isLeaveApproved ? undefined : updateDropDown}
                          disabled={isLeaveApproved}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            ...(isLeaveApproved && {
                              backgroundColor: "#f8f9fa",
                              color: "#6c757d",
                              cursor: "not-allowed"
                            })
                          }}
                        />
                      </div>
                      {/* To Date & Evening Period */}
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
                          {t("app.modals.addLeave.toDate")}
                        </label>
                        <DatePicker
                          name="EndDate"
                          selected={timeOffModal.EndDate ? new Date(timeOffModal.EndDate) : null}
                          peekNextMonth
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          placeholderText={t("app.modals.addLeave.selectEndDate")}
                          dateFormat="MM/dd/yyyy"
                          disabled={isLeaveApproved}
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            fontSize: "14px",
                            transition: "border-color 0.3s ease",
                            ...(isLeaveApproved && {
                              backgroundColor: "#f8f9fa",
                              color: "#6c757d",
                              cursor: "not-allowed"
                            })
                          }}
                          onChange={isLeaveApproved ? undefined : (date) => handleDateChange("EndDate", date)}
                        />
                        <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: 8, marginTop: 12, display: "block" }}>
                          {t("app.modals.addLeave.eveningPeriod")}
                        </label>
                        <DropDownList
                          className="time-off-modal-dropdown"
                          selection
                          defaultValue={0}
                          options={endingLeaveOptions}
                          fluid
                          name="EndingLeave"
                          value={timeOffModal.EndingLeave}
                          onChange={isLeaveApproved ? undefined : updateDropDown}
                          disabled={isLeaveApproved}
                          style={{
                            border: "1px solid #e9ecef",
                            borderRadius: "8px",
                            ...(isLeaveApproved && {
                              backgroundColor: "#f8f9fa",
                              color: "#6c757d",
                              cursor: "not-allowed"
                            })
                          }}
                        />
                      </div>
                    </div>

                    {/* Comments */}
                    <Form.Field>
                      <label style={{ fontSize: "0.9rem", fontWeight: 600, color: "#495057", marginBottom: "8px", marginTop: "12px", display: "block" }}>
                        {t("app.modals.addLeave.comments")}
                      </label>
                      <TextArea
                        name="LeaveNote"
                        value={timeOffModal.LeaveNote || timeOffModal.leaveNote || timeOffModal.Reason || timeOffModal?.leaveRequestComment?.leaveNote || ""}
                        placeholder={t("app.modals.addLeave.commentsPlaceholder")}
                        onChange={isLeaveApproved ? undefined : onChange}
                        disabled={isLeaveApproved}
                        maxLength={1000}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: "1px solid #e9ecef",
                          borderRadius: "8px",
                          fontSize: "14px",
                          minHeight: "100px",
                          resize: "vertical",
                          transition: "border-color 0.3s ease",
                          ...(isLeaveApproved && {
                            backgroundColor: "#f8f9fa",
                            color: "#6c757d",
                            cursor: "not-allowed"
                          })
                        }}
                      />
                      <div style={{ fontSize: "0.75rem", color: "#6c757d", marginTop: "4px", textAlign: "right" }}>
                        {t("app.modals.addLeave.charactersCount", { count: (timeOffModal.LeaveNote || timeOffModal.leaveNote || timeOffModal.Reason || timeOffModal?.leaveRequestComment?.leaveNote || "").length })}
                      </div>
                    </Form.Field>

                    {/* Action Buttons */}
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "24px" }}>
                      <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}>
                        <button
                          onClick={handleCloseModal}
                          style={{ padding: "12px 24px", border: "1px solid #6c757d", borderRadius: "8px", background: "white", color: "#6c757d", fontSize: "14px", fontWeight: "600", cursor: "pointer", transition: "all 0.3s ease" }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f8f9fa";
                            e.currentTarget.style.borderColor = "#5a6268";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "white";
                            e.currentTarget.style.borderColor = "#6c757d";
                          }}
                        >
                          {t("app.modals.addLeave.close")}
                        </button>
                        {!isPublicHoliday && !isLeaveApproved && (
                          <button
                            onClick={onSave}
                            className="btn-brand"
                            disabled={isButtonDisabled || isFormLoading}
                            style={{
                              opacity: (isButtonDisabled || isFormLoading) ? 0.6 : 1,
                              cursor: (isButtonDisabled || isFormLoading) ? 'not-allowed' : 'pointer',
                              ...((isButtonDisabled || isFormLoading) && {
                                background: '#6c757d',
                                borderColor: '#6c757d'
                              })
                            }}
                          >
                            {isFormLoading ? (
                              <FetchingSpinner text={t("app.modals.addLeave.saving")} inline />
                            ) : (
                              isEditMode ? t("app.modals.addLeave.updateLeave") : t("app.modals.addLeave.addLeaveToCalendar")
                            )}
                          </button>
                        )}
                        {!isPublicHoliday && (isLeaveApproved || isLeavePending || (isEditMode && isAdmin && isApproverIdNull)) && (
                          <button
                            onClick={handleCancelLeave}
                            style={{
                              padding: "12px 24px",
                              border: "1px solid #dc3545",
                              borderRadius: "8px",
                              background: "#dc3545",
                              color: "white",
                              fontSize: "14px",
                              fontWeight: "600",
                              cursor: "pointer",
                              transition: "all 0.3s ease"
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#c82333";
                              e.currentTarget.style.borderColor = "#bd2130";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#dc3545";
                              e.currentTarget.style.borderColor = "#dc3545";
                            }}
                          >
                            {isLeavePending ? t("app.modals.addLeave.cancelPendingLeave") : t("app.modals.addLeave.cancelLeave")}
                          </button>
                        )}
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </Dimmer.Dimmable>
          </div>
        </div>
      )}

      {/* Conflicts Details Modal */}
      {showConflictsModal && validation && validation.result && validation.result.conflicts && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: 'blur(2px)',
            zIndex: 2000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowConflictsModal(false);
            }
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '500px',
              maxHeight: 'min(80vh, 80dvh)',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Modal Header */}
            <div style={{
              padding: '20px 24px 16px',
              borderBottom: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.2em' }}>⚠️</span>
                <h4 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600', color: '#495057' }}>
                  {t("app.modals.addLeave.leaveConflictsDetails")}
                </h4>
              </div>
              <button
                onClick={() => setShowConflictsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#6c757d',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e9ecef';
                  e.currentTarget.style.color = '#495057';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#6c757d';
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content - scrollable */}
            <div style={{ padding: '20px 24px', flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <div style={{ marginBottom: '16px', color: '#6c757d', fontSize: '0.9rem' }}>
                Found {validation.result.conflicts?.length || 0} conflict{(validation.result.conflicts?.length || 0) > 1 ? 's' : ''} with existing leave requests:
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {(validation.result.conflicts || []).map((conflict, index) => (
                  <div key={index} style={{
                    padding: '16px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div style={{ fontWeight: '600', color: '#495057', fontSize: '1rem' }}>
                        {new Date(conflict.date).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <span style={{
                        fontSize: '0.8rem',
                        color: conflict.status === 'Approved' ? '#28a745' : '#ffc107',
                        backgroundColor: conflict.status === 'Approved' ? '#d4edda' : '#fff3cd',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontWeight: '500'
                      }}>
                        {conflict.status}
                      </span>
                    </div>

                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>Leave Type: </span>
                      <span style={{ color: '#6c757d' }}>{conflict.leaveType}</span>
                    </div>

                    <div style={{ marginBottom: '6px' }}>
                      <span style={{ fontWeight: '500', color: '#495057' }}>Period: </span>
                      <span style={{ color: '#6c757d' }}>
                        {conflict.startingLeave === 0 ? "Morning" : "Afternoon"} to {conflict.endingLeave === 0 ? "LunchTime" : "End of Day"}
                      </span>
                    </div>

                    {conflict.notes && (
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                        <span style={{ fontWeight: '500', color: '#495057', fontSize: '0.9rem' }}>Notes: </span>
                        <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>{conflict.notes}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '16px 24px',
              borderTop: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'flex-end',
              flexShrink: 0,
            }}>
              <button
                onClick={() => setShowConflictsModal(false)}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#5a6268';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#6c757d';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TImeOffModal;
